import { XP_TO_NEXT } from '@somnhold/content';
import type { CampaignState, ItemInstance } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { audio } from '../audio/sfx.ts';
import { levelGains } from './level-gains.ts';
import { itemCard, RUNE } from './panels.ts';
import type { LootKey } from './incursion-loot.ts';

type Hero = CampaignState['players'][number]['hero'];

/** What the hero looked like before the incursion, to tell what it brought back. */
export interface Before {
  storage: Record<LootKey, number>;
  level: number;
  xp: number;
  bag: number;
  relics: string[];
  /** Relics kept in reserve (won with both slots full). */
  kept: string[];
  embers: number;
  shards: number;
  /** Ids of every piece of gear the hero had (to show what is new). */
  items: number[];
}

export const snapshotBefore = (s: CampaignState, me: string): Before => {
  const hero = s.players.find((p) => p.id === me)?.hero as Hero;
  return { storage: { wood: s.storage.wood, stone: s.storage.stone, crystal: s.storage.crystal, dust: s.storage.dust, iron: s.storage.iron ?? 0 }, level: hero.level, xp: hero.xp, bag: pieces(hero), relics: [...hero.relics], kept: [...(hero.relicBag ?? [])], embers: hero.embers ?? 0, shards: hero.shards ?? 0, items: gearOf(hero).map((i) => i.id) };
};

const gearOf = (hero: Hero): ItemInstance[] => [hero.gear.weapon, hero.gear.armor, hero.gear.amulet, ...hero.bag].filter((i): i is ItemInstance => !!i);

/** A find, shown off: a burst of light in its rarity's color and the piece itself. */
const reveal = (inner: HTMLElement, color: string, label: string): HTMLElement =>
  h('div.loot-reveal', { style: `--rune:${color}` }, h('span.lr-rays'), h('span.lr-label', {}, label), inner);

/** One reveal per new piece of gear and per relic, for the end-of-run and wave cards. */
export const revealFinds = (s: CampaignState, me: string, items: ItemInstance[], relics: string[]): HTMLElement[] => {
  const p = s.players.find((x) => x.id === me);
  const cls = p?.hero.heroClass ?? 'warrior';
  const finds: HTMLElement[] = [];
  for (const item of items) {
    const worn = !!p && [p.hero.gear.weapon, p.hero.gear.armor, p.hero.gear.amulet].some((g) => g?.id === item.id);
    finds.push(reveal(h('div', {}, itemCard(item, cls, []), h('p.lr-where', {}, worn ? t('incEnd.worn') : t('incEnd.inBag'))), RUNE[item.rarity] ?? '#c4c8d2', t('incEnd.newItem', { r: t(`rarity.${item.rarity}`).toLowerCase() })));
  }
  for (const r of relics) {
    finds.push(
      reveal(
        h('div.lr-relic', {}, h('span.lr-ico', { html: (ICONS as Record<string, string>)[r] ?? ICONS.amulet }), h('div', {}, h('b', {}, t(`relic.${r}`)), h('p', {}, t(`relic.${r}.desc`)), p?.hero.relicBag?.includes(r as never) ? h('p.lr-where', {}, t('incEnd.relicKept')) : '')),
        '#c98bff',
        t('incEnd.newRelic'),
      ),
    );
  }
  return finds;
};

/** Gear worn plus gear carried (a find goes straight onto an empty slot). */
const pieces = (hero: Hero): number => [hero.gear.weapon, hero.gear.armor, hero.gear.amulet].filter(Boolean).length + hero.bag.length;

const totalXp = (level: number, xp: number): number => {
  let sum = xp;
  for (let l = 1; l < level; l++) sum += XP_TO_NEXT[l] ?? 0;
  return sum;
};

/**
 * The card at the end of an incursion: won or not, the haul, experience and any find. Back to the bastion on demand.
 * `heading` replaces the title and the line under it; `extra` goes under the haul (an expedition: what each one won).
 */
export const incursionResult = (
  s: CampaignState,
  me: string,
  before: Before,
  o: { won: boolean; place: string; onBack: () => void; crypt?: boolean; claimedBefore?: boolean; heading?: { title: string; sub: string }; extra?: HTMLElement[] },
): HTMLElement => {
  const player = s.players.find((p) => p.id === me);
  const hero = player?.hero as Hero;
  const gained = (['wood', 'stone', 'crystal', 'dust', 'iron'] as const).map((k) => [k, (s.storage[k] ?? 0) - before.storage[k]] as const).filter(([, v]) => v > 0);
  const xp = totalXp(hero.level, hero.xp) - totalXp(before.level, before.xp);
  const relics = [...hero.relics, ...(hero.relicBag ?? [])].filter((r) => !before.relics.includes(r) && !before.kept.includes(r));
  const items = pieces(hero) - before.bag;
  const lines: HTMLElement[] = [];
  if (xp > 0) lines.push(h('li', {}, h('span', { html: ICONS.hero }), t('incEnd.xp', { n: xp })));
  const embers = (hero.embers ?? 0) - before.embers;
  if (embers > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.ember }), t('incEnd.embers', { n: embers })));
  const shards = (hero.shards ?? 0) - (before.shards ?? 0);
  if (shards > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.shard }), t('incEnd.shards', { n: shards })));
  if (hero.level > before.level && player) {
    // What the new level gave, said plainly: the hero sheet shows the same for the next one.
    const gains = levelGains(player, before.level, hero.level);
    lines.push(h('li.up', { 'data-test': 'level-up' }, h('span', { html: ICONS.oath }), h('div', {}, t('incEnd.level', { n: hero.level }), gains.length ? h('div.small', {}, gains.join(' · ')) : '')));
  }
  // What was found, big: each new piece of gear and each relic gets its own reveal.
  const finds = revealFinds(s, me, gearOf(hero).filter((i) => !before.items.includes(i.id)), relics);
  if (items > 0 && !finds.length) lines.push(h('li.up', {}, h('span', { html: ICONS.sword }), t('incEnd.item')));
  if (finds.length) audio.play('victory', 0.5);
  const back = h('div.modal-back.inc-end');
  const go = (): void => {
    window.removeEventListener('keydown', onKey);
    back.remove();
    o.onBack();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === 'Escape') go();
  };
  window.addEventListener('keydown', onKey);
  back.append(
    h(
      `div.modal.ie-card${o.won ? '.won' : '.lost'}`,
      { role: 'dialog', 'aria-modal': 'true', 'data-test': 'inc-end' },
      h(
        'header',
        {},
        h('div.ie-mark', {}, o.won ? '✓' : '✗'),
        h('h2', {}, o.heading?.title ?? t(`${o.crypt ? 'cryptEnd' : 'incEnd'}.${o.won ? 'won' : 'lost'}`)),
        h('p', {}, o.heading?.sub ?? (o.won ? t(o.crypt ? 'cryptEnd.wonSub' : 'incEnd.wonSub', { place: o.place }) : t(o.crypt ? 'cryptEnd.lostSub' : 'incEnd.lostSub'))),
      ),
      h(
        'div.body',
        {},
        gained.length
          ? h('div.ie-loot', {}, ...gained.map(([k, v]) => h('span.loot', {}, h('span', { html: ICONS[k] }), h('b', {}, `+${v}`), h('span.small', {}, t(`res.${k}`)))))
          : h('p.hint', {}, t('incEnd.nothing')),
        gained.length ? h('p.small.ie-note', {}, t('incEnd.toStore')) : '',
        // A won crypt with today's chest already taken: only experience, and it is no bug.
        o.crypt && o.won && o.claimedBefore ? h('p.ie-claimed', { 'data-test': 'crypt-claimed' }, t('cryptEnd.claimed')) : '',
        lines.length ? h('ul.ie-lines', {}, ...lines) : '',
        ...finds,
        ...(o.extra ?? []),
      ),
      h('footer', {}, h('button.btn', { 'data-test': 'inc-back', onclick: go }, t('incEnd.back'))),
    ),
  );
  return back;
};
